/**
 * apps/server/client — Bannière d'enrôlement : « ce serveur n'est pas encore relié »
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * ─── POURQUOI ELLE EST AUSSI VOYANTE ─────────────────────────────────────────
 * Le serveur DÉMARRE quand même sans être enrôlé — c'est un choix : les tickets doivent sortir,
 * même si la console est injoignable ou si personne n'a encore cliqué. Mais un logiciel qui
 * fonctionne « presque » sans le dire est un logiciel dont le défaut se découvre trois mois plus
 * tard, le jour où on en a besoin. Le client doit voir, dès le premier écran, que la supervision
 * à distance n'est PAS active — et savoir exactement quoi faire.
 *
 * D'où : bandeau haut, orange, permanent, non refermable tant que le serveur n'est pas relié.
 *
 * Elle s'affiche AVANT toute connexion (l'écran de login est le premier endroit regardé), d'où
 * un `GET /api/enrollment` public — qui ne divulgue ni jeton, ni clé, ni code.
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * La bannière ne s'affiche QUE sur les écrans de gestion. Liste d'INCLUSION, pas d'exclusion.
 *
 * Deux raisons, et la seconde compte plus que la première :
 *
 * 1. `/display` (salle d'attente) et `/distributor` (borne) sont face au PUBLIC. Un bandeau orange
 *    « ce serveur n'est pas relié à l'assistance » devant les patients n'apporte rien à personne :
 *    il inquiète des gens qui n'y peuvent rien et abîme l'image du client.
 *
 * 2. Surtout : une liste d'exclusion est un piège à retardement. Le jour où l'on ajoute un écran
 *    public — un mur d'affichage, une seconde borne — il hériterait de la bannière PAR DÉFAUT, et
 *    personne ne s'en apercevrait avant que le client n'appelle. Ici, un nouvel écran n'affiche
 *    rien tant qu'on ne l'a pas explicitement inscrit. Le défaut est le silence.
 *
 * Et de toute façon, seul un administrateur PEUT saisir le code (le POST l'exige) : la montrer
 * ailleurs qu'à lui ne servirait qu'à inquiéter des gens sans pouvoir d'action.
 */
const ECRANS_DE_GESTION = ['/manage'];

const S = {
  barre: {
    background: 'linear-gradient(90deg,#B45309,#D97706)', color: '#fff',
    padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12,
    flexWrap: 'wrap', fontSize: '.92rem', boxShadow: '0 2px 6px rgba(0,0,0,.2)',
  },
  ok: { background: 'linear-gradient(90deg,#166534,#16A34A)' },
  champ: {
    padding: '6px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,.5)',
    background: 'rgba(255,255,255,.12)', color: '#fff', width: 190,
    fontFamily: 'ui-monospace,monospace', letterSpacing: '.08em', textTransform: 'uppercase',
  },
  bouton: {
    padding: '6px 14px', borderRadius: 6, border: 0, cursor: 'pointer',
    background: '#fff', color: '#B45309', fontWeight: 700,
  },
};

export default function EnrollmentBanner() {
  const { pathname } = useLocation();
  const [etat, setEtat] = useState(null);
  const [code, setCode] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');

  const charger = async () => {
    try {
      const r = await fetch('/api/enrollment').then((x) => x.json());
      if (r?.success) setEtat(r.data);
    } catch { /* serveur qui redémarre : on retentera */ }
  };

  useEffect(() => {
    charger();
    // On re-sonde : après le clic de l'administrateur sur la console, la bannière doit disparaître
    // TOUTE SEULE. Sans cela, le client resterait devant un bandeau orange déjà obsolète et
    // rappellerait pour rien — exactement le coup de fil qu'on cherche à supprimer.
    const t = setInterval(charger, 15000);
    return () => clearInterval(t);
  }, []);

  if (!ECRANS_DE_GESTION.some((p) => pathname.startsWith(p))) return null;
  if (!etat || !etat.disponible) return null;   // binaire sans console : pas de bannière
  if (etat.state === 'enrole') return null;      // relié : plus rien à dire

  const envoyer = async (e) => {
    e.preventDefault();
    setEnvoi(true); setErreur('');
    try {
      const r = await fetch('/api/enrollment/code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({ code }),
      }).then((x) => x.json());

      if (!r.success) {
        // 401/403 : le POST est réservé à l'admin — et ce n'est pas une tracasserie. Saisir un
        // code, c'est décider à quelle console ce serveur ouvrira son relais d'administration.
        setErreur(r.error || 'Refusé — connectez-vous en administrateur pour saisir le code.');
      } else {
        setEtat(r.data); setCode('');
      }
    } catch (err) {
      setErreur(err.message);
    } finally {
      setEnvoi(false);
    }
  };

  if (etat.state === 'en_attente') {
    return (
      <div style={{ ...S.barre, ...S.ok }}>
        <strong>⏳ Demande envoyée — en attente de validation.</strong>
        <span style={{ opacity: 0.9 }}>
          Vous n’avez plus rien à faire : dès que votre demande est approuvée, ce serveur se
          reliera <strong>tout seul</strong>. Vous pouvez travailler normalement.
        </span>
      </div>
    );
  }

  // Le lien a été COUPÉ par le prestataire. Formulation distincte de « pas encore relié » : le
  // client sait qu'il l'était, et un message identique le laisserait croire à une panne de son
  // installation. On dit ce qui s'est passé, et que ses tickets n'en souffrent pas.
  const revoque = etat.state === 'revoque';

  return (
    <div style={S.barre}>
      <strong style={{ fontSize: '1rem' }}>
        {revoque
          ? '⚠️ Le lien avec l’assistance a été interrompu.'
          : '⚠️ Ce serveur n’est pas encore relié à l’assistance.'}
      </strong>
      <span style={{ opacity: 0.95 }}>
        {revoque
          ? <>Vos tickets fonctionnent <strong>normalement</strong> et rien n’est perdu. Seules la
            supervision et l’assistance à distance sont interrompues. Pour les rétablir, saisissez
            le nouveau code fourni par votre prestataire :</>
          : <>Les tickets fonctionnent normalement, mais la supervision et l’assistance à distance
            sont <strong>inactives</strong>. Saisissez le code fourni par votre prestataire :</>}
      </span>
      <form onSubmit={envoyer} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          style={S.champ}
          placeholder="MJSE-XXXX-XXXX-XXXX"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          spellCheck={false}
        />
        <button style={S.bouton} disabled={envoi || code.trim().length < 4} type="submit">
          {envoi ? 'Envoi…' : 'Relier'}
        </button>
      </form>
      {(erreur || etat.lastError) && (
        <span style={{ background: 'rgba(0,0,0,.25)', padding: '4px 10px', borderRadius: 6 }}>
          {erreur || etat.lastError}
        </span>
      )}
    </div>
  );
}
